import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { keyCode } from '@bingads-webui/keycode';
import { Input } from '@fluentui/react-components';
import { Edit16Regular, Dismiss16Regular, Checkmark16Regular } from '@fluentui/react-icons';
import { useInfoPaneItemRenameStyle } from '../../styles';

const spinnerStyle = ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
});

export const InfoItemRename = ({
  i18n,
  name,
  renameAsync,
  renderBlocker,
  inputPlaceholderText,
  renameTitleText,
  isReadonly,
}) => {
  const classes = useInfoPaneItemRenameStyle();

  const [displayInputBox, setDisplayInputBox] = useState(false);
  const [inputName, setInputName] = useState('');
  const [showUiBlocker, setShowUiBlocker] = useState(false);

  useEffect(() => {
    setDisplayInputBox(false);
    setShowUiBlocker(false);
    setInputName(name);
  }, [name]);

  const handleRename = useCallback(() => {
    setShowUiBlocker(true);
    renameAsync(inputName).finally(() => {
      setShowUiBlocker(false);
      setDisplayInputBox(false);
    });
  }, [inputName, renameAsync]);

  const onEnterRename = useCallback((event) => {
    if (event && event.keyCode === keyCode.Enter) {
      handleRename();
    }
  }, [handleRename]);

  const handleDisplayInputBox = useCallback(() => {
    setInputName(name || '');
    setDisplayInputBox(true);
  }, [name]);

  const onEnterEditIcon = useCallback((event) => {
    if (event && event.keyCode === keyCode.Enter) {
      handleDisplayInputBox();
    }
  }, [handleDisplayInputBox]);

  const handleCancel = useCallback(() => {
    setInputName('');
    setDisplayInputBox(false);
  }, []);

  const onEnterCancel = useCallback((event) => {
    if (event && event.keyCode === keyCode.Enter) {
      handleCancel();
    }
  });

  const onEnterPress = useCallback((event) => {
    if (event && event.keyCode === keyCode.Enter && inputName !== name) {
      handleRename();
    }
  }, [handleRename, inputName, name]);

  const onInputChange = useCallback((ev, data) => {
    const { value } = data || {};
    setInputName(value);
  }, []);

  return (
    <div className={classnames('name-content', classes.nameContentBox)}>
      {
        displayInputBox ?
          <div className={classnames(classes.editNameBox)}>
            <Input
              className={classnames('input-box', classes.textField)}
              defaultValue={name || ''}
              placeholder={name ? '' : inputPlaceholderText}
              ariaLabel={i18n.getString(_TL_('rename'))}
              onChange={onInputChange}
              maxLength={512}
              autoFocus
              onKeyDown={onEnterPress}
            />
            <Checkmark16Regular className={classnames('complete-rename', classes.completeIcon)} role="button" tabIndex={0} aria-label={i18n.getString(_TL_('complete'))} onKeyDown={onEnterRename} onClick={handleRename} />
            <Dismiss16Regular className={classnames('cancel-rename', classes.cancelIcon)} role="button" tabIndex={0} aria-label={i18n.getString(_TL_('cancel'))} onKeyDown={onEnterCancel} onClick={handleCancel} />
          </div> :
          <div>
            <div className={classes.addNameBox}>
              <div className={classes.addNameTitle}>{renameTitleText}</div>
              {!isReadonly && <Edit16Regular role="button" tabIndex={0} aria-label={i18n.getString(_TL_('rename'))} className={classnames('rename-box', classes.addNameIcon)} onKeyDown={onEnterEditIcon} onClick={handleDisplayInputBox} />}
            </div>
            <div className={classnames(classes.nameBox, 'name-wrapper')}>
              {name || '-'}
            </div>
          </div>
        }
      {
        showUiBlocker && renderBlocker && renderBlocker({ spinnerStyle })
      }
    </div>
  );
};

InfoItemRename.propTypes = {
  name: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  renameAsync: PropTypes.func.isRequired,
  renderBlocker: PropTypes.func.isRequired,
  inputPlaceholderText: PropTypes.string.isRequired,
  isReadonly: PropTypes.bool,
};

InfoItemRename.defaultProps = {
  isReadonly: false,
};
